/**
 * Hard hat stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_everyday_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const hardHatStickersPack: ToilePack = {
    slug: 'hard-hat-stickers',
    category: 'sticker',
    name: 'Hard hat stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'safety', label: 'Safety badge', subject: 'a round badge with a hard hat and the words "Safety First" on a yellow and black hazard-stripe ring'},
        {key: 'trade', label: 'Trade emblem', subject: 'a shield with a crossed wrench and lightning bolt and the word "Electrician" on a ribbon'},
        {key: 'joke', label: 'Joke label', subject: 'a rectangular label reading "I Void Warranties" beside a small cartoon screwdriver'},
    ],
    styles: [
        {key: 'safety', label: 'Safety sign', style: 'industrial safety-sign sticker, bold sans-serif lettering and hard-edged shapes, reflective-sheeting look', palette: 'a high-visibility palette of yellow, black and white', framing: 'Centered, with a thin white die-cut border and an even margin of background all round.'},
        {key: 'cartoon', label: 'Cartoon', style: 'cartoon workwear sticker, rounded shapes, thick black outlines, smooth gradient-free fills', palette: 'a bright limited palette of five flat colors', framing: 'Centered, with a thick white die-cut border and an even margin of background all round.'},
        {key: 'union', label: 'Union patch', style: 'embroidered-patch look sticker, stitched border and woven thread texture, hard edges', palette: 'a palette of navy, white, red and gold', framing: 'Centered, with a stitched rim and an even margin of background all round.'},
    ],
    directions: ['a welder', 'orange', 'bad pun'],
    directionHint: 'try: a welder',
    seo: {
        title: 'Hard Hat Stickers: Safety, Trade and Joke Labels',
        metaDescription: 'Nine hard hat sticker designs: a safety badge, a trade emblem and a joke label, in safety sign, cartoon and union patch styles.',
        h1: 'Hard hat stickers: safety badge, trade emblem, joke label',
        tagline: 'Safety, trade and humour stickers for a hard hat, in three finishes.',
        intro: "Construction workers, electricians and welders mark their hard hats with stickers about safety, trade and humour. These nine die-cut designs show a round Safety First badge on a hazard-stripe ring, a shield for the electrician's trade with a crossed wrench and lightning bolt, and a joke label that reads I Void Warranties beside a cartoon screwdriver. Each comes in a safety sign, cartoon or embroidered union patch style. Type a request, such as a welder, to shape it.",
        useCases: [
            'a sticker for a site hard hat',
            'a gift for a tradesperson',
            'a team sticker for a crew',
            'a funny label for a work helmet',
        ],
        faq: [
            {q: 'Which sticker is the safety one?', a: 'The safety badge is the round Safety First design, with a hard hat and a hazard-stripe ring.'},
            {q: 'Do the trade emblems show real company logos?', a: 'No. The trade emblem is a generic shield with a wrench and a bolt, and the word Electrician on a ribbon.'},
            {q: 'What are the three styles?', a: 'Safety sign uses bold lettering in yellow, black and white, cartoon uses thick outlines and five flat colours, and union patch uses navy, white, red and gold with a stitched rim.'},
        ],
    },
}
